/* Orion universal buybox.
   variant mode : the product's own options drive variant selection.
   flavour mode : tiles switch between sibling products (whey isolates).
   Purchase options come from the product's real selling plans. */
(() => {
  const decode = (s) => {
    if (typeof s !== 'string' || s.indexOf('&') === -1) return s;
    const ta = document.createElement('textarea');
    ta.innerHTML = s;
    return ta.value;
  };

  class OrionBuybox extends HTMLElement {
    connectedCallback() {
      const readJSON = (sel) => {
        const el = this.querySelector(sel);
        if (!el) return null;
        try { return JSON.parse(el.textContent); } catch (e) { return null; }
      };
      this.product = readJSON('[data-o-product]');
      if (!this.product) return;
      this.flavours = readJSON('[data-o-flavours]') || [];
      this.bundle = readJSON('[data-o-bundle]');
      this.mode = this.dataset.mode === 'flavour' && this.flavours.length > 1 ? 'flavour' : 'variant';
      this.strings = window.orionPdpStrings || {};

      const active = this.mode === 'flavour'
        ? (this.flavours.find((f) => f.current) || this.flavours[0])
        : null;

      this.state = {
        selected: (this.product.options || []).map((o, i) => {
          const v = this.product.variants.find((x) => x.available) || this.product.variants[0];
          return v ? v.options[i] : (o.values[0] || '');
        }),
        img: 0,
        variantImage: null,
        plan: this.product.plans.length ? 'sub' : 'once',
        cadence: 0,
        qty: 1,
        flavour: active ? this.flavours.indexOf(active) : 0,
        bags: [],
      };

      this.els = {
        main: this.querySelector('[data-o-main]'),
        thumbs: this.querySelector('[data-o-thumbs]'),
        price: this.querySelector('[data-o-price]'),
        options: this.querySelector('[data-o-options]'),
        flavourTiles: this.querySelector('[data-o-flavour-tiles]'),
        flavourName: this.querySelector('[data-o-flavour-name]'),
        plans: this.querySelector('[data-o-plans]'),
        cadence: this.querySelector('[data-o-cadence]'),
        cadencePills: this.querySelector('[data-o-cadence-pills]'),
        gifts: this.querySelector('[data-o-gifts]'),
        qty: this.querySelector('[data-o-qty]'),
        cta: this.querySelector('[data-o-cta]'),
        alt: this.querySelector('[data-o-alt]'),
        error: this.querySelector('[data-o-error]'),
        stock: this.querySelector('[data-o-stock]'),
      };

      this.querySelector('[data-o-qty-dec]').addEventListener('click', () => { this.state.qty = Math.max(1, this.state.qty - 1); this.render(); });
      this.querySelector('[data-o-qty-inc]').addEventListener('click', () => { this.state.qty = Math.min(10, this.state.qty + 1); this.render(); });
      this.syncImageToVariant();
      this.els.cta.addEventListener('click', () => this.addToCart());
      /* the one-time option is a full plan card; the small alt toggle under
         the CTA was competing copy and is gone from the markup */
      if (this.els.alt) {
        this.els.alt.addEventListener('click', () => {
          this.state.plan = this.state.plan === 'once' ? 'sub' : 'once';
          this.render();
        });
      }
      this.render();
    }

    t(key, fallback, vars) {
      let s = decode(this.strings[key] || fallback);
      if (vars) s = s.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? vars[k] : m));
      return s;
    }

    money(c) { return '$' + (c / 100).toFixed(2); }

    /* the gallery/plans source: sibling product in flavour mode, else this product */
    get source() {
      return this.mode === 'flavour' ? this.flavours[this.state.flavour] : this.product;
    }

    get currentVariant() {
      const p = this.product;
      if (!p.variants.length) return null;
      if (!p.options.length || (p.options.length === 1 && p.options[0].values.length === 1)) return p.variants[0];
      return p.variants.find((v) => v.options.every((o, i) => o === this.state.selected[i])) || p.variants[0];
    }

    get images() {
      const src = this.source;
      return (src.images && src.images.length) ? src.images : this.product.images;
    }

    /* Jump the gallery to the selected variant's image. Variant and gallery
       URLs carry different width params, so match on the CDN path only. */
    syncImageToVariant() {
      const v = this.currentVariant;
      if (!v) return;
      const key = v.imageKey || (v.image ? v.image.split('?')[0] : null);
      if (!key) return;
      const imgs = this.images;
      const hit = imgs.findIndex((im) => (im.key || im.src.split('?')[0]) === key);
      if (hit > -1) {
        this.state.img = hit;
      } else if (v.image) {
        /* variant image isn't in the gallery list: show it as a virtual first slide */
        this.state.variantImage = v.image;
        this.state.img = -1;
      }
    }

    get plans() {
      return this.mode === 'flavour' ? (this.source.plans || []) : this.product.plans;
    }

    /* A "pack tier" option is one whose every value reads as a pack count
       ("12 Travel Packs", "36 Travel Packs"). It gets the design's tier tiles
       instead of text pills: big count, per-pack price, and value badges. */
    packTierIndex() {
      const opts = this.product.options || [];
      for (let i = 0; i < opts.length; i++) {
        const vals = opts[i].values || [];
        if (vals.length > 1 && vals.every((v) => /\d+\s*(travel\s*)?packs?\b/i.test(v))) return i;
      }
      return -1;
    }

    packCount(value) {
      const m = String(value || '').match(/(\d+)/);
      return m ? parseInt(m[1], 10) : 0;
    }

    /* Flavour choices for the 3-month builder, derived from the bundle's own
       variant titles ("A / B / C"), so nothing has to be configured twice.
       Chip images are borrowed from this product's variant images by matching
       the flavour name, scaled down at the CDN. */
    bagFlavours() {
      if (this._bagFlavours) return this._bagFlavours;
      const seen = [];
      (this.bundle.variants || []).forEach((v) => {
        String(v.key || '').split(' / ').forEach((part) => {
          const name = part.trim();
          if (name && !seen.includes(name)) seen.push(name);
        });
      });
      const imgFor = (name) => {
        const pv = (this.product.variants || []).find(
          (x) => (x.options || []).includes(name) && x.image
        );
        return pv ? pv.image.replace(/width=\d+/, 'width=120') : null;
      };
      this._bagFlavours = seen.map((n) => ({ name: n, img: imgFor(n) }));
      return this._bagFlavours;
    }

    /* Unordered multiset match: three bags are the same box whatever the tap
       order, but each ordering is a distinct Shopify variant title. */
    resolveBundleVariant() {
      const norm = (arr) => arr.slice().sort().join('|');
      const want = norm(this.state.bags);
      return (this.bundle.variants || []).find(
        (v) => norm(String(v.key || '').split(' / ').map((x) => x.trim())) === want
      ) || null;
    }

    renderBagBuilder() {
      const s = this.state;
      const bags = this.bundle.bags || 3;
      if (s.bags.length !== bags) s.bags = Array.from({ length: bags }, () => null);
      const flavours = this.bagFlavours();

      const wrap = document.createElement('div');
      wrap.className = 'o-buy__bags';

      const head = document.createElement('div');
      head.className = 'o-buy__bags-head';
      const title = document.createElement('strong');
      title.textContent = this.t('bagsTitle', 'Build your 3 bags: pick any combination');
      head.appendChild(title);
      const left = s.bags.filter((b) => !b).length;
      if (left) {
        const count = document.createElement('span');
        count.className = 'o-buy__bags-count';
        count.textContent = this.t('bagsMore', 'Add {n} more', { n: left });
        head.appendChild(count);
      }
      wrap.appendChild(head);

      const slots = document.createElement('div');
      slots.className = 'o-buy__bag-slots';
      s.bags.forEach((bag, i) => {
        const slot = document.createElement('button');
        slot.type = 'button';
        slot.className = 'o-buy__bag' + (bag ? ' is-filled' : '');
        const lab = document.createElement('span');
        lab.className = 'o-buy__bag-label';
        lab.textContent = this.t('bagLabel', 'Bag {n}', { n: i + 1 });
        slot.appendChild(lab);
        if (bag) {
          const fl = flavours.find((f) => f.name === bag);
          if (fl && fl.img) {
            const im = document.createElement('img');
            im.src = fl.img; im.alt = ''; im.loading = 'lazy';
            slot.appendChild(im);
          }
          const nm = document.createElement('span');
          nm.className = 'o-buy__bag-name'; nm.textContent = bag;
          slot.appendChild(nm);
          const x = document.createElement('span');
          x.className = 'o-buy__bag-x'; x.textContent = '\u00d7';
          x.setAttribute('aria-hidden', 'true');
          slot.appendChild(x);
          slot.addEventListener('click', (e) => {
            e.stopPropagation();
            s.bags[i] = null;
            this.render();
          });
        } else {
          const plus = document.createElement('span');
          plus.className = 'o-buy__bag-plus'; plus.textContent = '+';
          plus.setAttribute('aria-hidden', 'true');
          slot.appendChild(plus);
          const nm = document.createElement('span');
          nm.className = 'o-buy__bag-name is-empty';
          nm.textContent = this.t('bagChoose', 'Choose a flavor');
          slot.appendChild(nm);
        }
        slots.appendChild(slot);
      });
      wrap.appendChild(slots);

      const opts = document.createElement('div');
      opts.className = 'o-buy__bag-opts';
      flavours.forEach((f) => {
        const n = s.bags.filter((b) => b === f.name).length;
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'o-buy__bag-opt' + (n ? ' is-on' : '');
        if (f.img) {
          const im = document.createElement('img');
          im.src = f.img; im.alt = ''; im.loading = 'lazy';
          b.appendChild(im);
        }
        b.appendChild(document.createTextNode(f.name));
        const tally = document.createElement('span');
        tally.className = 'o-buy__bag-tally';
        tally.textContent = n ? '\u00d7' + n : '+';
        b.appendChild(tally);
        b.addEventListener('click', (e) => {
          e.stopPropagation();
          const idx = s.bags.findIndex((x) => !x);
          if (idx === -1) return;
          s.bags[idx] = f.name;
          this.render();
        });
        opts.appendChild(b);
      });
      wrap.appendChild(opts);

      const hint = document.createElement('p');
      hint.className = 'o-buy__bags-hint';
      hint.textContent = this.t('bagsHint', 'Want three of the same? Tap one flavor three times. Swap any bag before your next shipment.');
      wrap.appendChild(hint);

      /* clicks inside the builder must not re-select the plan card */
      wrap.addEventListener('click', (e) => e.stopPropagation());
      return wrap;
    }

    planDefs() {
      const v = this.currentVariant;
      const base = this.mode === 'flavour' ? this.source.price : (v ? v.price : this.product.variants[0]?.price || 0);
      let servings = this.product.servings || 25;
      let perKey = 'perServing';
      let perFallback = '{p} / serving';
      const ti = this.packTierIndex();
      if (ti !== -1) {
        const chosen = (v && v.options[ti]) || this.state.selected[ti];
        const count = this.packCount(chosen);
        if (count) { servings = count; perKey = 'perPack'; perFallback = '{p} / pack'; }
      }
      const defs = [];
      /* 3-Month Supply: the mix-and-match bundle as a purchase option. Prices
         come from the bundle product itself, so the SAVE badge and the
         strikethrough appear only once a selling plan is actually attached in
         StayAI; until then the card sells the convenience, not a discount. */
      if (this.bundle && this.bundle.price) {
        /* a real selling-plan discount wins; the merchant's display setting
           covers the gap until StayAI carries the prepaid plan */
        const bpct = this.bundle.planPct || this.bundle.displayPct || 0;
        const bprice = Math.round(this.bundle.price * (1 - bpct / 100));
        const bags = this.bundle.bags || 3;
        const bServings = servings * bags;
        defs.push({
          key: 'bundle',
          title: this.t('bundleTitle', '3-Month Supply'),
          save: bpct ? this.t('save', 'SAVE {n}%', { n: bpct }) : '',
          price: bprice,
          was: bpct ? this.bundle.price : 0,
          sub: this.t('bundleSub', 'Pick any {bags} flavors · {per} per bag, ships every 3 months', {
            bags: bags, per: this.money(Math.round(bprice / bags)),
          }),
          flag: bpct ? this.t('bundleFlag', 'BEST VALUE · LOWEST PRICE PER SERVING') : '',
          per: this.t('perServing', '{p} / serving', { p: this.money(Math.round(bprice / bServings)) }),
          perks: this.strings.bundlePerks || [],
        });
      }
      const plans = this.plans;
      if (plans.length) {
        const plan = plans[this.state.cadence] || plans[0];
        const pct = plan.pct || 0;
        const price = Math.round(base * (1 - pct / 100));
        /* StayAI plans usually discount order 1 harder than renewals: pct is
           the first-order adjustment, pct2 the ongoing one. The renewal price
           feeds the {renew} perk line so it always matches the live plan. */
        const renewPct = plan.pct2 !== undefined ? plan.pct2 : pct;
        const renewPrice = this.money(Math.round(base * (1 - renewPct / 100)));
        const perkVars = { renew: renewPrice, renewOff: renewPct };
        defs.push({
          key: 'sub',
          title: this.t('subTitle', 'Subscribe & Save'),
          save: pct ? this.t('save', 'SAVE {n}%', { n: pct }) : '',
          price, was: pct ? base : 0,
          sub: this.t('subSub', 'Delivered on repeat · no contracts'),
          flag: this.t('subFlag', 'MOST POPULAR'),
          per: servings > 1 ? this.t(perKey, perFallback, { p: this.money(Math.round(price / servings)) }) : '',
          perks: (this.strings.subPerks || []).map((line) =>
            String(line).replace(/\{(\w+)\}/g, (m, k) => (perkVars[k] !== undefined ? perkVars[k] : m))
          ),
        });
      }
      defs.push({
        key: 'once',
        title: this.t('onceTitle', 'One-Time Purchase'),
        save: '', price: base, was: 0,
        sub: this.t('onceSub', 'Ships once'),
        flag: '',
        per: servings > 1 ? this.t(perKey, perFallback, { p: this.money(Math.round(base / servings)) }) : '',
        perks: this.strings.oncePerks || [],
      });
      return defs;
    }

    render() {
      const s = this.state;

      /* ---- gallery ---- */
      const imgs = this.images;
      if (imgs.length) {
        const virtual = s.img === -1 && s.variantImage;
        const img = virtual
          ? { src: s.variantImage, alt: this.product.title }
          : imgs[Math.min(Math.max(s.img, 0), imgs.length - 1)];
        const existing = this.els.main.querySelector('img');
        if (existing) {
          existing.src = img.src;
          existing.alt = img.alt || this.product.title;
          existing.removeAttribute('srcset');
        }
        this.els.thumbs.replaceChildren(...imgs.slice(0, 6).map((im, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'o-buy__thumb' + (i === s.img ? ' is-active' : '');
          b.setAttribute('role', 'listitem');
          b.setAttribute('aria-label', (im.alt || this.product.title) + ' ' + (i + 1));
          const t = document.createElement('img');
          t.src = im.thumb; t.alt = ''; t.loading = 'lazy';
          b.appendChild(t);
          b.addEventListener('click', () => { s.img = i; this.render(); });
          return b;
        }));
      }

      /* ---- flavour tiles (cross-product) ---- */
      if (this.mode === 'flavour' && this.els.flavourTiles) {
        this.els.flavourName.textContent = this.source.name;
        this.els.flavourTiles.replaceChildren(...this.flavours.map((fl, i) => {
          /* Siblings really are separate products, so these stay anchors: they
             must be crawlable, right-clickable and openable in a new tab. The
             click is intercepted for a soft swap — see softSwap for why the
             whole main region is replaced rather than just this component. */
          const el = document.createElement(fl.current ? 'button' : 'a');
          if (fl.current) { el.type = 'button'; } else { el.href = fl.url; }
          if (!fl.current) {
            el.addEventListener('click', (event) => {
              /* let the browser handle modified clicks as real navigations */
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
              event.preventDefault();
              this.softSwap(fl.url, el);
            });
          }
          el.className = 'o-buy__flavour' + (fl.current ? ' is-on' : '');
          el.setAttribute('role', 'radio');
          el.setAttribute('aria-checked', fl.current ? 'true' : 'false');
          if (fl.chip) {
            const im = document.createElement('img');
            im.src = fl.chip; im.alt = ''; im.loading = 'lazy';
            el.appendChild(im);
          }
          el.appendChild(document.createTextNode(fl.name));
          return el;
        }));
      }

      /* ---- the product's own option pickers ---- */
      if (this.mode === 'variant' && this.els.options) {
        const opts = this.product.options || [];
        const meaningful = opts.filter((o) => !(o.values.length === 1 && /default/i.test(o.values[0])));
        this.els.options.replaceChildren(...meaningful.map((opt) => {
          const idx = opts.indexOf(opt);
          /* Image swatches when the option's values have distinct variant
             photos (flavours, colourways). Falls back to text pills for
             options like Size where every value shares one image. */
          const imageFor = (value) => {
            const v = this.product.variants.find((x) => x.options[idx] === value && x.image);
            return v ? v.image : null;
          };
          const swatches = opt.values.map(imageFor);
          const distinct = new Set(swatches.filter(Boolean)).size;
          const useImages = distinct >= 2 && distinct === opt.values.length;

          const group = document.createElement('div');
          group.className = 'o-buy__optgroup';
          const label = document.createElement('p');
          label.className = 'o-buy__step-title';
          const labelText = (useImages && idx === 0 && this.strings.stepFlavor)
            ? this.strings.stepFlavor
            : opt.name;
          label.appendChild(document.createTextNode(labelText));
          const val = document.createElement('span');
          val.className = 'o-buy__opt-value';
          val.textContent = s.selected[idx] || '';
          label.appendChild(val);
          group.appendChild(label);

          /* pack tiers: the design's step-1 tiles with per-pack pricing */
          if (idx === this.packTierIndex()) {
            const tiers = document.createElement('div');
            tiers.className = 'o-buy__tiers';
            tiers.setAttribute('role', 'radiogroup');
            tiers.setAttribute('aria-label', opt.name);
            opt.values.forEach((value, vi) => {
              const on = s.selected[idx] === value;
              const count = this.packCount(value);
              /* price follows the other choices already made (e.g. flavour) */
              const variant = this.product.variants.find((x) =>
                x.options[idx] === value &&
                x.options.every((o, oi) => oi === idx || !s.selected[oi] || o === s.selected[oi])
              ) || this.product.variants.find((x) => x.options[idx] === value);
              const b = document.createElement('button');
              b.type = 'button';
              b.setAttribute('role', 'radio');
              b.setAttribute('aria-checked', on ? 'true' : 'false');
              b.className = 'o-buy__tier' + (on ? ' is-on' : '');
              if (opt.values.length >= 3) {
                const tag = vi === opt.values.length - 1
                  ? this.t('tierValue', 'BEST VALUE')
                  : (vi === opt.values.length - 2 ? this.t('tierPopular', 'MOST POPULAR') : '');
                if (tag) {
                  const tg = document.createElement('span');
                  tg.className = 'o-buy__tier-tag'; tg.textContent = tag;
                  b.appendChild(tg);
                }
              }
              const qty = document.createElement('span');
              qty.className = 'o-buy__tier-qty'; qty.textContent = count;
              b.appendChild(qty);
              const unit = document.createElement('span');
              unit.className = 'o-buy__tier-unit';
              unit.textContent = this.t('tierPacks', 'packs');
              b.appendChild(unit);
              if (variant && count) {
                const per = document.createElement('span');
                per.className = 'o-buy__tier-per';
                per.textContent = this.t('perPack', '{p} / pack', { p: this.money(Math.round(variant.price / count)) });
                b.appendChild(per);
              }
              b.addEventListener('click', () => {
                s.selected[idx] = value;
                this.syncImageToVariant();
                this.render();
              });
              tiers.appendChild(b);
            });
            group.appendChild(tiers);
            return group;
          }

          const wrap = document.createElement('div');
          wrap.className = useImages ? 'o-buy__flavours' : 'o-buy__optvalues';
          wrap.setAttribute('role', 'radiogroup');
          wrap.setAttribute('aria-label', opt.name);

          opt.values.forEach((value, vi) => {
            const on = s.selected[idx] === value;
            const anyAvailable = this.product.variants.some((x) => x.options[idx] === value && x.available);
            const b = document.createElement('button');
            b.type = 'button';
            b.setAttribute('role', 'radio');
            b.setAttribute('aria-checked', on ? 'true' : 'false');
            if (useImages) {
              b.className = 'o-buy__flavour' + (on ? ' is-on' : '') + (anyAvailable ? '' : ' is-out');
              const im = document.createElement('img');
              im.src = swatches[vi];
              im.alt = '';
              im.loading = 'lazy';
              b.appendChild(im);
              b.appendChild(document.createTextNode(value));
            } else {
              b.className = 'o-buy__optbtn' + (on ? ' is-on' : '') + (anyAvailable ? '' : ' is-out');
              b.textContent = value;
            }
            b.addEventListener('click', () => {
              s.selected[idx] = value;
              this.syncImageToVariant();
              this.render();
            });
            wrap.appendChild(b);
          });
          group.appendChild(wrap);
          return group;
        }));
      }

      /* ---- price ---- */
      const defs = this.planDefs();
      if (!defs.some((d) => d.key === s.plan)) s.plan = defs[0].key;
      const active = defs.find((d) => d.key === s.plan);
      if (this.els.price) {
        this.els.price.textContent = this.money(active.price);
        if (active.was) {
          const st = document.createElement('s');
          st.textContent = this.money(active.was);
          this.els.price.appendChild(st);
        }
      }

      /* ---- plans ---- */
      this.els.plans.replaceChildren(...defs.map((d) => {
        const el = document.createElement('div');
        el.className = 'o-buy__plan' + (d.key === s.plan ? ' is-on' : '');
        el.setAttribute('role', 'radio');
        el.setAttribute('aria-checked', d.key === s.plan ? 'true' : 'false');
        el.tabIndex = 0;
        /* Ribbon sits on the card's top edge, above the padding, so it reads as
           a label on the card rather than a line of copy inside it. */
        if (d.flag) {
          const rb = document.createElement('span');
          rb.className = 'o-buy__plan-ribbon';
          rb.textContent = d.flag;
          el.appendChild(rb);
          el.classList.add('has-ribbon');
        }
        const head = document.createElement('div');
        head.className = 'o-buy__plan-head';
        /* An explicit radio: three stacked cards with only a tint to separate
           chosen from unchosen leaves people hunting for what is selected. */
        const dot = document.createElement('span');
        dot.className = 'o-buy__plan-dot';
        dot.setAttribute('aria-hidden', 'true');
        head.appendChild(dot);
        const title = document.createElement('span');
        title.className = 'o-buy__plan-title'; title.textContent = d.title;
        head.appendChild(title);
        if (d.save) {
          const sv = document.createElement('span');
          sv.className = 'o-buy__plan-save'; sv.textContent = d.save;
          head.appendChild(sv);
        }
        const priceCol = document.createElement('span');
        priceCol.className = 'o-buy__plan-pricecol';
        const pr = document.createElement('span');
        pr.className = 'o-buy__plan-price';
        pr.textContent = this.money(d.price) + ' ';
        if (d.was) {
          const w = document.createElement('s');
          w.className = 'o-buy__plan-was'; w.textContent = this.money(d.was);
          pr.appendChild(w);
        }
        priceCol.appendChild(pr);
        if (d.per) {
          const per = document.createElement('span');
          per.className = 'o-buy__plan-per'; per.textContent = d.per;
          priceCol.appendChild(per);
        }
        head.appendChild(priceCol);
        el.appendChild(head);
        const sub = document.createElement('p');
        sub.className = 'o-buy__plan-sub'; sub.textContent = d.sub;
        el.appendChild(sub);

        /* unselected cards stay compact: the perk list belongs to the choice
           you have made, not to every option on the pile */
        if (d.perks && d.perks.length && d.key === s.plan) {
          const ul = document.createElement('ul');
          ul.className = 'o-buy__plan-perks';
          d.perks.forEach((p) => { const li = document.createElement('li'); li.textContent = decode(p); ul.appendChild(li); });
          el.appendChild(ul);
        }
        if (d.key === s.plan && d.key === 'sub') {
          if (this.els.cadence) el.appendChild(this.els.cadence);
          if (this.els.gifts) el.appendChild(this.els.gifts);
        }
        if (d.key === s.plan && d.key === 'bundle') {
          el.appendChild(this.renderBagBuilder());
          if (this.els.gifts) el.appendChild(this.els.gifts);
        }
        const pick = () => { s.plan = d.key; this.render(); };
        el.addEventListener('click', pick);
        el.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } });
        return el;
      }));

      /* single-option products don't need a purchase-options heading fight:
         hide the whole step when there is only a one-time option */
      const stepEl = this.els.plans.closest('.o-buy__step');
      if (stepEl) stepEl.hidden = defs.length < 2;

      /* ---- cadence ---- */
      const plans = this.plans;
      const cadOn = s.plan === 'sub' && plans.length > 1;
      this.els.cadence.hidden = !cadOn;
      if (cadOn) {
        this.els.cadencePills.replaceChildren(...plans.map((p, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'o-buy__pill' + (i === s.cadence ? ' is-on' : '');
          b.textContent = p.name.replace('*', '');
          b.addEventListener('click', () => { s.cadence = i; this.render(); });
          return b;
        }));
      }

      if (this.els.gifts) this.els.gifts.hidden = s.plan !== 'sub' && s.plan !== 'bundle';

      /* ---- CTA ---- */
      const v = this.currentVariant;
      const soldOut = !v || !v.available;

      /* tell the rest of the page which variant is selected, so sections like
         "what's in the box" can follow the chosen flavour */
      const label = this.mode === 'flavour'
        ? this.source.name
        : (this.product.options || []).map((o, i) => s.selected[i])
            .filter((x) => x && !/default/i.test(x)).join(' · ');
      const sig = (v ? v.id : '') + '|' + label;
      if (sig !== this._lastSig) {
        this._lastSig = sig;
        document.dispatchEvent(new CustomEvent('orion:variant:changed', {
          detail: {
            variantId: v ? v.id : null,
            label: label,
            title: this.product.title,
            image: (v && v.image) || (imgs[0] && imgs[0].src) || null,
          },
        }));
      }
      this.els.qty.textContent = s.qty;
      /* the bundle's bags are built inline in its card; the CTA stays a
         truthful add button that unlocks when the box is complete */
      if (s.plan === 'bundle') {
        const left = s.bags.filter((b) => !b).length;
        /* Sourced from this product, a complete box is simply three chosen
           flavours we hold variants for; only the legacy mix-and-match SKU
           needs a single combined variant to exist. */
        const resolved = left !== 0
          ? null
          : (this.bundle.ownProduct
            ? (s.bags.every((f) => (this.bundle.variants || [])
                .some((v) => String(v.key).trim() === f)) || null)
            : this.resolveBundleVariant());
        if (left > 0) {
          this.els.cta.disabled = true;
          this.els.cta.textContent = s.bags.some(Boolean)
            ? this.t('bagsMore', 'Add {n} more', { n: left })
            : this.t('bundleCta', 'Choose your 3 flavors');
        } else if (!resolved) {
          this.els.cta.disabled = true;
          this.els.cta.textContent = this.t('bagsUnavailable', 'That combination is sold out');
        } else {
          this.els.cta.disabled = false;
          this.els.cta.textContent = (this.bundle.planId
            ? this.t('subCta', 'Start subscription')
            : this.t('onceCta', 'Add to cart')) + ' · ' + this.money(active.price);
        }
      } else {
        this.els.cta.disabled = soldOut;
        this.els.cta.textContent = soldOut
          ? this.t('soldOut', 'Sold out')
          : (s.plan === 'once' ? this.t('onceCta', 'Add to cart') : this.t('subCta', 'Start subscription'))
            + ' · ' + this.money(active.price * s.qty);
      }
      if (this.els.stock) this.els.stock.hidden = soldOut;
      if (this.els.alt) {
        const hasSub = defs.some((d) => d.key === 'sub');
        this.els.alt.hidden = !hasSub;
        if (hasSub) {
          const pct = (plans[s.cadence] || plans[0] || {}).pct || 0;
          this.els.alt.textContent = s.plan === 'once'
            ? this.t('altSub', 'Subscribe instead and save {n}%', { n: pct })
            : this.t('altOnce', 'Buy once for {p}', { p: this.money(defs.find((d) => d.key === 'once').price) });
        }
      }
      this.els.error.hidden = true;
    }

    /* Swap to a sibling flavour without a page load.

       The whole #MainContent is replaced, not just this buybox, because the
       reviews, "what's in the box", nutrition and story sections are all
       rendered per product: swapping the buy panel alone would leave a page
       selling Cinnamon Roll while showing Cookies and Cream reviews.

       Custom elements upgrade themselves when inserted, so orion-buybox and
       friends re-initialise for free. The plain-IIFE scripts (reviews, buy bar)
       already listen for shopify:section:load, so one dispatch reboots them. */
    async softSwap(url, tile) {
      const main = document.querySelector('#MainContent');
      if (!main) { window.location.assign(url); return; }

      tile.classList.add('is-loading');
      try {
        const res = await fetch(url, { headers: { 'X-Requested-With': 'orion-buybox' } });
        if (!res.ok) throw new Error('fetch failed');
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const next = doc.querySelector('#MainContent');
        if (!next) throw new Error('no main');

        const top = this.getBoundingClientRect().top;
        main.replaceWith(next);
        history.pushState({}, '', url);
        if (doc.title) document.title = doc.title;

        /* hold the buy panel where it was, so the swap reads as an update
           rather than a jump to the top of a new page */
        const fresh = document.querySelector('orion-buybox');
        if (fresh) window.scrollBy(0, fresh.getBoundingClientRect().top - top);

        document.dispatchEvent(new CustomEvent('shopify:section:load'));
      } catch (err) {
        window.location.assign(url);
      }
    }

    async addToCart() {
      const s = this.state;
      let items;
      if (s.plan === 'bundle' && this.bundle && this.bundle.ownProduct) {
        /* Three months of this product is three bags, one line per chosen
           flavour, each carrying the 3-month plan. A single mix-and-match
           variant would price off a different SKU. */
        const chosen = (s.bags || []).filter(Boolean);
        if (chosen.length !== (this.bundle.bags || 3)) return;
        const byKey = {};
        (this.bundle.variants || []).forEach((v) => { byKey[String(v.key).trim()] = v.id; });
        const tally = {};
        chosen.forEach((f) => { tally[f] = (tally[f] || 0) + 1; });
        items = Object.keys(tally)
          .filter((f) => byKey[f])
          .map((f) => ({ id: byKey[f], quantity: tally[f], selling_plan: this.bundle.planId }));
        if (!items.length) return;
      } else {
        let v;
        if (s.plan === 'bundle' && this.bundle) {
          v = this.resolveBundleVariant();
          if (!v) return;
        } else {
          v = this.currentVariant;
        }
        if (!v) return;
        const item = { id: v.id, quantity: s.qty };
        if (s.plan === 'bundle' && this.bundle.planId) {
          item.selling_plan = this.bundle.planId;
        }
        items = [item];
      }
      const item = items[0];
      if (s.plan === 'sub') {
        const plans = this.plans;
        const plan = plans[s.cadence] || plans[0];
        if (plan) item.selling_plan = plan.id;
      }
      this.els.cta.classList.add('is-loading');
      this.els.error.hidden = true;
      try {
        const sectionIds = [...document.querySelectorAll('cart-items-component')]
          .map((el) => el.dataset.sectionId).filter(Boolean);
        const body = { items };
        if (sectionIds.length) body.sections = sectionIds.join(',');
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const resp = await r.json();
        if (!r.ok) throw new Error(resp.description || this.t('addFail', 'Could not add to cart. Please try again.'));
        document.dispatchEvent(new CustomEvent('orion:cart:added', { detail: { sections: resp.sections } }));
      } catch (err) {
        this.els.error.textContent = err.message;
        this.els.error.hidden = false;
      } finally {
        this.els.cta.classList.remove('is-loading');
      }
    }
  }

  if (!customElements.get('orion-buybox')) customElements.define('orion-buybox', OrionBuybox);

  /* pushState without this leaves Back doing nothing on a soft-swapped page */
  window.addEventListener('popstate', () => {
    if (document.querySelector('orion-buybox')) window.location.reload();
  });
})();

/* ============================================================
   Nutrition label + unboxing dialogs.

   Both buttons are rendered by Liquid only when the matching
   product metafield exists, so this simply wires whatever is on
   the page. The video iframe is built on open and torn down on
   close: mounting it upfront would have every PDP loading a
   YouTube player nobody asked for.
   ============================================================ */
(() => {
  const embed = (url) => {
    const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
    if (yt) return '<iframe src="https://www.youtube.com/embed/' + yt[1] + '?autoplay=1" allow="autoplay; encrypted-media" allowfullscreen title="Unboxing"></iframe>';
    const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vm) return '<iframe src="https://player.vimeo.com/video/' + vm[1] + '?autoplay=1" allow="autoplay; fullscreen" allowfullscreen title="Unboxing"></iframe>';
    return '<video src="' + url + '" controls autoplay playsinline></video>';
  };

  function wire(root) {
    const open = (dialog) => dialog && !dialog.open && dialog.showModal();

    const nutritionBtn = root.querySelector('[data-o-nutrition]');
    const nutritionDlg = root.querySelector('[data-o-nutrition-dialog]');
    if (nutritionBtn && nutritionDlg) nutritionBtn.addEventListener('click', () => open(nutritionDlg));

    const unboxBtn = root.querySelector('[data-o-unboxing]');
    const unboxDlg = root.querySelector('[data-o-unboxing-dialog]');
    if (unboxBtn && unboxDlg) {
      const slot = unboxDlg.querySelector('[data-o-unboxing-slot]');
      unboxBtn.addEventListener('click', () => {
        if (slot) slot.innerHTML = embed(unboxBtn.dataset.src || '');
        open(unboxDlg);
      });
      // stop playback on close, however the dialog was dismissed
      unboxDlg.addEventListener('close', () => { if (slot) slot.innerHTML = ''; });
    }

    root.querySelectorAll('[data-o-media-close]').forEach((b) =>
      b.addEventListener('click', () => b.closest('dialog').close())
    );
    root.querySelectorAll('.o-buy__media-dialog').forEach((d) =>
      d.addEventListener('click', (e) => { if (e.target === d) d.close(); })
    );
  }

  const boot = () => document.querySelectorAll('orion-buybox, [data-o-media-root]').forEach(wire);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();

/* subscribe jump: scrolls to the featured-drop bridge and opens it, so the
   subscription panel is unmissable even when it lives below the fold */
document.addEventListener('click', (e) => {
  const b = e.target.closest('[data-o-subjump]');
  if (!b) return;
  const drop = document.querySelector('.o-drop');
  const toggle = document.querySelector('[data-o-drop-toggle]');
  const anchor = drop || document.querySelector('[data-o-drop-target]');
  if (!anchor) return;
  anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
  if (toggle && toggle.getAttribute('aria-expanded') !== 'true') {
    setTimeout(() => toggle.click(), 450);
  }
});
